import { useMemo } from 'react';

import { useAuth } from '../auth/AuthContext';

/**
 * Hook to check permissions for the current user.
 *
 * @returns {{
 *   permissions: string[],
 *   hasPermission: (perm: string) => boolean,
 *   hasAnyPermission: (perms: string[]) => boolean,
 *   hasAllPermissions: (perms: string[]) => boolean,
 *   isStaff: boolean,
 * }}
 */
export function usePermissions() {
    const { user } = useAuth();
    const permissions = useMemo(() => user?.permissions ?? [], [user?.permissions]);
    const isStaff = Boolean(user?.staff);

    return useMemo(
        () => ({
            permissions,
            isStaff,
            hasPermission(perm) {
                if (!perm) return false;
                if (isStaff) return true;
                return permissions.includes(perm);
            },
            hasAnyPermission(perms) {
                if (!perms?.length) return false;
                if (isStaff) return true;
                return perms.some((p) => permissions.includes(p));
            },
            hasAllPermissions(perms) {
                if (!perms?.length) return false;
                if (isStaff) return true;
                return perms.every((p) => permissions.includes(p));
            },
        }),
        [permissions, isStaff],
    );
}
